/**
 * V10 Phase C — 에피소드 density heatmap (GitHub contribution calendar 스타일).
 *
 * 각 bucket 을 grid 의 작은 square cell 로 렌더. 빈도는 5 단계 discrete shade 로 매핑.
 * Hover 시 bucket 시작 시각 + episode 개수를 title 로 표시.
 *
 * props:
 *  - buckets: 시간순 정렬된 bucket 배열
 *  - rows:    grid 행 수 (기본 5). cols 는 buckets 수 / rows 로 자동 계산
 */

type Props = {
	readonly buckets: ReadonlyArray<{ readonly bucket: string; readonly count: number }>
	readonly rows?: number
}

/** `count` 를 전체 최댓값 대비 5단계(0~4) discrete level 로 변환. GitHub 잔디와 동일. */
function toLevel(count: number, max: number): 0 | 1 | 2 | 3 | 4 {
	if (count <= 0) return 0
	const ratio = count / max
	if (ratio <= 0.25) return 1
	if (ratio <= 0.5) return 2
	if (ratio <= 0.75) return 3
	return 4
}

/** bucket ISO → 한국어 날짜/시간 라벨. 시간 단위가 섞여도 안전하게 포맷. */
function formatBucketLabel(iso: string): string {
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return iso
	return d.toLocaleString("ko-KR", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit"
	})
}

/** 시간축 라벨 — `1991.09` 같은 연-월. 짧은 서비스 수명이면 `MM.DD` 까지. */
function formatAxisLabel(iso: string): string {
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return ""
	const yyyy = d.getFullYear()
	const mm = String(d.getMonth() + 1).padStart(2, "0")
	return `${yyyy}.${mm}`
}

/**
 * ms duration 을 "≈ 1일" / "≈ 3시간" / "≈ 2주" / "≈ 5분" 형태로 축약.
 * buckets 는 서비스 수명(첫~마지막 episode 구간)을 N 등분한 결과라, 셀 하나의 시간 폭이
 * 서비스에 따라 가변이다. 헤더에 이 단위를 명시해 "일 단위인가 주 단위인가" 혼동을 없앤다.
 */
function formatCellUnit(ms: number): string {
	if (!Number.isFinite(ms) || ms <= 0) return ""
	const minute = 60 * 1000
	const hour = 60 * minute
	const day = 24 * hour
	const week = 7 * day
	if (ms >= week * 2) return `≈ ${Math.round(ms / week)}주`
	if (ms >= day) {
		const d = ms / day
		return `≈ ${d < 10 ? d.toFixed(1) : Math.round(d)}일`
	}
	if (ms >= hour) return `≈ ${Math.round(ms / hour)}시간`
	return `≈ ${Math.round(ms / minute)}분`
}

export function TimelineHeatmap(props: Props) {
	const rows = props.rows ?? 7
	const total = props.buckets.length
	if (total === 0) {
		return (
			<div
				className="timeline-heatmap timeline-heatmap--empty"
				aria-label="에피소드 밀도 데이터 없음"
				title="이 서비스에 aggregate 할 episode 가 아직 없습니다"
			/>
		)
	}
	const cols = Math.ceil(total / rows)
	const max = Math.max(1, ...props.buckets.map((b) => b.count))
	const firstLabel = formatAxisLabel(props.buckets[0].bucket)
	const midLabel = formatAxisLabel(props.buckets[Math.floor(total / 2)].bucket)
	const lastLabel = formatAxisLabel(props.buckets[total - 1].bucket)
	const firstMs = Date.parse(props.buckets[0].bucket)
	const lastMs = Date.parse(props.buckets[total - 1].bucket)
	const perCellMs = Number.isFinite(firstMs) && Number.isFinite(lastMs) ? (lastMs - firstMs) / total : 0
	const unitLabel = formatCellUnit(perCellMs)

	// bucket i → col = floor(i / rows), row = i % rows. 좌→우로 시간 증가.
	return (
		<div
			className="timeline-heatmap-wrap"
			title="에피소드 밀도 — 시간대별로 attach 된 episode 수 (짙을수록 많음). 슬라이더의 asOf 와 같은 시간축."
		>
			<div className="timeline-heatmap-header">
				<span className="timeline-heatmap-title">
					📅 밀도 {unitLabel && <span className="timeline-heatmap-unit">· 각 셀 {unitLabel}</span>}
				</span>
				<div className="timeline-heatmap-legend">
					<span className="timeline-heatmap-legend-label">적음</span>
					<span className="timeline-heatmap-cell timeline-heatmap-cell--l0" aria-hidden />
					<span className="timeline-heatmap-cell timeline-heatmap-cell--l1" aria-hidden />
					<span className="timeline-heatmap-cell timeline-heatmap-cell--l2" aria-hidden />
					<span className="timeline-heatmap-cell timeline-heatmap-cell--l3" aria-hidden />
					<span className="timeline-heatmap-cell timeline-heatmap-cell--l4" aria-hidden />
					<span className="timeline-heatmap-legend-label">많음</span>
				</div>
			</div>
			<div
				className="timeline-heatmap"
				role="img"
				aria-label={`${total}개 시간 구간의 episode 밀도 히트맵 (${firstLabel} ~ ${lastLabel})`}
				style={{
					gridTemplateColumns: `repeat(${cols}, 1fr)`,
					gridTemplateRows: `repeat(${rows}, 1fr)`
				}}
			>
				{props.buckets.map((b, i) => {
					const col = Math.floor(i / rows)
					const row = i % rows
					const level = toLevel(b.count, max)
					return (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: positional bucket index
							key={i}
							className={`timeline-heatmap-cell timeline-heatmap-cell--l${level}`}
							style={{ gridColumn: col + 1, gridRow: row + 1 }}
							title={`${formatBucketLabel(b.bucket)} · ${b.count} episode${b.count === 1 ? "" : "s"}`}
						/>
					)
				})}
			</div>
			<div className="timeline-heatmap-axis" aria-hidden>
				<span>{firstLabel}</span>
				<span>{midLabel}</span>
				<span>{lastLabel}</span>
			</div>
		</div>
	)
}
